import {
  outcomeLabels,
  validateDecomposition,
  validatePmf,
  type OutcomeSpace,
  type Pmf,
} from "@sooth-labs/idl";
import { composeBranchQuestion } from "./branchQuestion.js";
import { outcomeSpaceOfRun } from "./outcomeSpace.js";
import { TOWER_TOLERANCE } from "./towerTolerance.js";
import type { ForecastEvent, Run } from "./model.js";

export interface SourceEventIdentity {
  question: string;
  resolutionCriteria: string;
  resolutionDay: string;
  resolutionTime: string;
}

export interface DerivedSourceSnapshot {
  reportRowKey: string | null;
  event: {
    id: string;
    identity: SourceEventIdentity;
    w: number;
    a: number;
    b: number;
  };
  parentProbability: number;
}

/** The server compares each snapshot field whole against the source it re-reads. */
export interface DerivedMultiwaySourceSnapshot {
  reportRowKey: string | null;
  event: {
    id: string;
    identity: SourceEventIdentity;
    w: number;
    a_pmf: Record<string, number>;
    b_pmf: Record<string, number>;
  };
  parentMass: number[];
}

interface MultiwayHeadline {
  space: OutcomeSpace;
  labels: string[];
  mass: number[];
}

type DerivableParent = Pick<Run, "status" | "forecastId" | "contract"> &
  Partial<Pick<Run, "history" | "serverPrediction">>;

function boundedProbability(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
}

function canonicalDay(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  const day = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const instant = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(instant.getTime()) || instant.toISOString().slice(0, 10) !== day) return null;
  if (raw.length > 10 && Number.isNaN(Date.parse(raw))) return null;
  return day;
}

function canonicalResolutionTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  const day = canonicalDay(raw);
  if (!day) return null;
  if (raw.length === 10) return `${day}T00:00:00.000Z`;
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(raw)) return null;
  const instant = new Date(raw);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

export function sourceEventIdentity(event: ForecastEvent): SourceEventIdentity | null {
  const question = typeof event.question === "string" ? event.question.trim() : "";
  const resolutionCriteria =
    typeof event.resolution_criteria === "string" ? event.resolution_criteria.trim() : "";
  const resolutionValue = event.resolution_time || event.date_end || event.date;
  const resolutionDay = canonicalDay(resolutionValue);
  const resolutionTime = canonicalResolutionTime(resolutionValue);
  if (!question || !resolutionCriteria || !resolutionDay || !resolutionTime) return null;
  return { question, resolutionCriteria, resolutionDay, resolutionTime };
}

export function sameSourceEvent(
  event: ForecastEvent | null | undefined,
  forkEvent: Partial<ForecastEvent> | null | undefined,
): boolean {
  const eventId = event?.id;
  const frozenEventId = forkEvent?._sourceEventId;
  if (
    !event ||
    !forkEvent ||
    typeof eventId !== "string" ||
    !eventId.trim() ||
    typeof frozenEventId !== "string" ||
    !frozenEventId.trim() ||
    frozenEventId !== eventId
  )
    return false;
  return sameSourceTerms(event, forkEvent);
}

/**
 * Does `event` carry the terms `forkEvent` froze for its source trigger? Unlike
 * `sameSourceEvent` it ignores the event id, which a fresh run may renumber while the terms
 * (and so the trigger's contract) stay the same.
 */
export function sameSourceTerms(
  event: ForecastEvent | null | undefined,
  forkEvent: Partial<ForecastEvent> | null | undefined,
): boolean {
  const current = event ? sourceEventIdentity(event) : null;
  const frozen = forkEvent?._sourceEventIdentity as SourceEventIdentity | null | undefined;
  return (
    !!current &&
    !!frozen &&
    current.question === frozen.question &&
    current.resolutionCriteria === frozen.resolutionCriteria &&
    current.resolutionDay === frozen.resolutionDay &&
    current.resolutionTime === frozen.resolutionTime
  );
}

/**
 * Keys are the canonical `outcomeLabels(space)`, the names the forecaster keys `a_pmf`/`b_pmf`
 * by, never the humanized display labels of a numeric space. `mass` overrides the contract's own
 * mass with a headline read from the live report.
 */
function multiwayHeadline(contract: unknown, mass?: unknown): MultiwayHeadline | null {
  const forecastValue = (contract as { forecast_value?: unknown } | null | undefined)
    ?.forecast_value as
    | { outcome_space?: unknown; representation?: { form?: unknown; mass?: unknown } }
    | undefined;
  const space = forecastValue?.outcome_space as OutcomeSpace | undefined;
  if (!space || typeof space !== "object" || space.kind === "boolean") return null;
  if (forecastValue?.representation?.form !== "pmf") return null;
  const headlineMass = mass ?? forecastValue.representation.mass;
  if (!Array.isArray(headlineMass)) return null;
  try {
    const labels = outcomeLabels(space);
    validatePmf(headlineMass, space);
    return { space, labels, mass: [...headlineMass] };
  } catch {
    return null;
  }
}

function massInLabelOrder(pmf: unknown, headline: MultiwayHeadline): number[] | null {
  if (!pmf || typeof pmf !== "object" || Array.isArray(pmf)) return null;
  const branch = pmf as Record<string, unknown>;
  if (Object.keys(branch).length !== headline.labels.length) return null;
  if (!headline.labels.every((label) => Object.hasOwn(branch, label))) return null;
  const mass = headline.labels.map((label) => branch[label]);
  if (!mass.every((slot): slot is number => typeof slot === "number")) return null;
  try {
    validatePmf(mass, headline.space);
    return mass;
  } catch {
    return null;
  }
}

function coherentMultiway(event: ForecastEvent, headline: MultiwayHeadline | null): boolean {
  if (!headline || !boundedProbability(event.p_event)) return false;
  const occurs = massInLabelOrder(event.a_pmf, headline);
  const otherwise = massInLabelOrder(event.b_pmf, headline);
  if (!occurs || !otherwise) return false;
  // `validatePmf` has already proven every vector non-empty (its length is the support size).
  const pmf = (mass: number[]): Pmf => ({ form: "pmf", mass: mass as Pmf["mass"] });
  try {
    validateDecomposition(
      pmf(headline.mass),
      { p_occurs: event.p_event, if_occurs: pmf(occurs), if_not: pmf(otherwise) },
      TOWER_TOLERANCE,
    );
    return true;
  } catch {
    return false;
  }
}

export function isMultiwayDerivation(parent: DerivableParent | null | undefined): boolean {
  return !!outcomeSpaceOfRun(parent);
}

export function derivedSense(newValue: number): "occurs" | "not_occurs" {
  return newValue >= 0.9 ? "occurs" : "not_occurs";
}

export function isDerivable(
  event: ForecastEvent | null | undefined,
  parent: DerivableParent | null | undefined,
  modType: string,
  newValue: unknown,
): boolean {
  if (modType !== "probability" || !event || !parent) return false;
  if (typeof event.id !== "string" || !event.id.trim()) return false;
  if (!boundedProbability(newValue)) return false;
  if (parent.status !== "completed" || !parent.forecastId) return false;
  if (!canonicalDay(event.resolution_time)) return false;
  if (!sourceEventIdentity(event)) return false;
  // Never use `serverPrediction` (a multiway row persists 0 there) or scalar a/b. A binary
  // vintage over a preserved multiway space finds no distribution here and keeps the run path.
  if (isMultiwayDerivation(parent)) {
    return coherentMultiway(event, multiwayHeadline(parent.contract));
  }
  if (
    !boundedProbability(parent.serverPrediction) &&
    !boundedProbability(parent.contract?.probability)
  ) {
    return false;
  }
  return [event.p_event, event.a, event.b].every(boundedProbability);
}

export function derivedDisplayQuestion(
  event: ForecastEvent,
  parent: Pick<Run, "contract" | "question"> | { contract?: unknown; question?: string },
  newValue: number,
): string {
  const contract =
    parent.contract && typeof parent.contract === "object"
      ? (parent.contract as { question?: string })
      : null;
  return composeBranchQuestion({
    sense: derivedSense(newValue),
    trigger: {
      title: String(event.title || event.question || "").trim(),
      date: String(event.resolution_time || event.date_end || event.date || "")
        .trim()
        .slice(0, 10),
    },
    parentQuestion: contract?.question || parent.question || "",
  });
}

export function derivedSourceSnapshot(
  parent: Pick<Run, "contract"> & Partial<Pick<Run, "serverPrediction" | "history">>,
  event: ForecastEvent,
  {
    reportRowKey = null,
    parentProbability: selectedParentProbability,
    parentMass: selectedParentMass,
  }: {
    reportRowKey?: string | null;
    parentProbability?: number | null;
    /** A multiway parent's headline mass read from the live report; the row's own otherwise. */
    parentMass?: number[] | null;
  } = {},
): DerivedSourceSnapshot | DerivedMultiwaySourceSnapshot | null {
  const identity = sourceEventIdentity(event);
  const resolutionDay = canonicalDay(event.resolution_time);
  if (outcomeSpaceOfRun(parent)) {
    // Freeze the headline the server will re-read (the report's, when one was read), and only
    // when the trigger is still coherent with it — a moved report is a refusal, not a guess.
    const headline = multiwayHeadline(parent.contract, selectedParentMass ?? undefined);
    if (
      !identity ||
      !resolutionDay ||
      typeof event.id !== "string" ||
      !event.id ||
      !headline ||
      !coherentMultiway(event, headline)
    ) {
      return null;
    }
    return {
      reportRowKey,
      event: {
        id: event.id,
        identity,
        w: event.p_event as number,
        a_pmf: event.a_pmf as Record<string, number>,
        b_pmf: event.b_pmf as Record<string, number>,
      },
      parentMass: headline.mass,
    };
  }
  const parentProbability = boundedProbability(selectedParentProbability)
    ? selectedParentProbability
    : boundedProbability(parent.serverPrediction)
      ? parent.serverPrediction
      : parent.contract?.probability;
  if (
    !identity ||
    !resolutionDay ||
    typeof event.id !== "string" ||
    !event.id ||
    !boundedProbability(event.p_event) ||
    !boundedProbability(event.a) ||
    !boundedProbability(event.b) ||
    !boundedProbability(parentProbability)
  ) {
    return null;
  }
  return {
    reportRowKey,
    event: {
      id: event.id,
      identity,
      w: event.p_event,
      a: event.a,
      b: event.b,
    },
    parentProbability,
  };
}

const TERMINAL_TRIGGER_STATUSES = new Set(["ready", "failed"]);

/**
 * A client-side pre-filter only: the server decides eligibility (lane, parent, multiway) and
 * generates at most once per branch.
 */
export function needsDerivedTriggers(run: Partial<Run> | null | undefined): boolean {
  if (!run || run.isOwn === false || run.status !== "completed") return false;
  if (typeof run.forecastId !== "string" || !run.forecastId) return false;
  if (run.detailPending || run.clearedAt) return false;
  const derived = run.forkDerived as { a?: unknown; b?: unknown } | null | undefined;
  if (!derived || !boundedProbability(derived.a) || !boundedProbability(derived.b)) return false;
  if (!Array.isArray(run.events) || run.events.length > 0) return false;
  if (TERMINAL_TRIGGER_STATUSES.has(run.derivedTriggersStatus ?? "")) return false;
  // Mirabilis lanes keep their existing derive behaviour; the server refuses them too.
  return !/mirabilis/i.test(String(run.backend ?? ""));
}
